import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  BlockStack,
  Box,
  Button,
  ButtonGroup,
  Card,
  Collapsible,
  Divider,
  Icon,
  InlineStack,
  ProgressBar,
  Select,
  Text
} from "@shopify/polaris";
import {
  ClipboardIcon,
  ImportIcon,
  ProductReferenceIcon,
  SearchIcon,
  StoreImportIcon
} from "@shopify/polaris-icons";
import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useAppBridge } from "@shopify/app-bridge-react";
import { useNavigate } from "react-router-dom";
import { Context } from "../Context/Context.js";
import { openExternalUrl, openShopifyPageInNewWindow } from "../utils/urlUtils.js";
import { countryList, majorCurrencyList } from "../utils/CurrencyList.js";
import { chromeExtensionExpectedVersion, chromeExtensionMessageTargetID, isShopifyTestHost } from "../ShopifyConstants.js";
import { i18 } from "../AppUtil.js";
import { getStoredTrialEligibility } from "../utils/subscriptionTrialUtils.js";

const GET_USER_EXTENSION_KEY = gql`
  query getUserExtensionKey {
    getUserExtensionKey
  }
`;

const GET_ONBOARDING_PRODUCT_STATUS = gql`
  query getOnboardingProductStatus {
    getOnboardingProductStatus
  }
`;

const SET_SHIP_TO_COUNTRY_AND_SETTING_FOURPX_SHIPPING_OPTIONS = gql`
  mutation setShipToCountryAndSettingFourPXShippingOptions($shipToCountry: String) {
    setShipToCountryAndSettingFourPXShippingOptions(shipToCountry: $shipToCountry){
      status
      message
    }
  }
`;

const SAVE_SETTING_CURRENCY = gql`
  mutation saveSettingProduct($currency: String) {
    saveSettingProduct(currency: $currency) {
      status
      message
    }
  }
`;

const CHROME_EXTENSION_URL = "https://chromewebstore.google.com/detail/cartifind-importer/lnbknopcbaaajalpagleljjonlblliim";

const hasValue = (value) => value != null && value !== "" && value !== "null" && value !== "undefined";

const hasActiveSubscription = (subscriptionType) => /^(Pro|Max|Ultra)(Monthly|Yearly)$/.test(String(subscriptionType || ""));

const ONBOARDING_GUIDE_VISIBILITY_KEY = "cartifindOnboardingGuideVisible";

const readSessionGuideVisible = () => {
  try {
    const stored = localStorage.getItem(ONBOARDING_GUIDE_VISIBILITY_KEY);
    if (stored === "0" || stored === "1") return stored === "1";
  } catch (_error) {
    // Missing storage still gets a first-paint decision below.
  }
  // Show on the first visit. A later status response must not insert or
  // remove the guide during this page view, or the product grid shifts.
  return true;
};

const getStoredGoal = () => {
  try {
    return localStorage.getItem("cartifindOnboardingGoal") || "import_products";
  } catch (_error) {
    return "import_products";
  }
};

const setStoredGoal = (goal) => {
  try {
    localStorage.setItem("cartifindOnboardingGoal", goal);
  } catch (_error) {
    // Ignore storage failures. The guide still works for the current session.
  }
};

const getStoredDefaultsReviewed = () => {
  try {
    return localStorage.getItem("cartifindOnboardingDefaultsReviewed") === "1";
  } catch (_error) {
    return false;
  }
};

const setStoredDefaultsReviewed = () => {
  try {
    localStorage.setItem("cartifindOnboardingDefaultsReviewed", "1");
  } catch (_error) {
    // Ignore storage failures. The review can still complete for this session.
  }
};

export default function GetStartedGuide({ onOpenImportByURL }) {
  const shopify = useAppBridge();
  const navigate = useNavigate();
  const { state, setState } = useContext(Context);
  const {
    productCount,
    orderCount,
    hasChromeExtension,
    isChromeExtensionLoggedIn,
    isMobile,
    sendLog
  } = state || {};

  const [guideVisible] = useState(readSessionGuideVisible);
  const [selectedGoal, setSelectedGoal] = useState(getStoredGoal);
  const [currentShipToCountry, setCurrentShipToCountry] = useState(() => localStorage.getItem("shipToCountry") || "");
  const [currentCurrency, setCurrentCurrency] = useState(() => localStorage.getItem("currency") || "");
  const [isConnectingExtension, setIsConnectingExtension] = useState(false);
  const [extensionConnectionMessage, setExtensionConnectionMessage] = useState("");
  const [showSetupPlan, setShowSetupPlan] = useState(false);
  const [hasReviewedProductDefaults, setHasReviewedProductDefaults] = useState(getStoredDefaultsReviewed);
  const [canFetchProductStatus, setCanFetchProductStatus] = useState(false);
  const [storedTrialEligibility] = useState(getStoredTrialEligibility);
  const hasLoggedView = React.useRef(false);
  const hasLoggedTrialView = React.useRef(false);
  const loggedStepEvents = React.useRef(new Set());
  const autoConnectAttemptedRef = React.useRef(false);
  const isConnectingExtensionRef = React.useRef(false);
  const [loadUserExtensionKey] = useLazyQuery(GET_USER_EXTENSION_KEY, {
    fetchPolicy: "cache-first",
  });
  useEffect(() => {
    const load = () => setCanFetchProductStatus(true);
    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      const idleId = window.requestIdleCallback(load, { timeout: 2500 });
      return () => window.cancelIdleCallback?.(idleId);
    }
    const timer = setTimeout(load, 1200);
    return () => clearTimeout(timer);
  }, []);
  const { data: productStatusData, loading: isLoadingProductStatus } = useQuery(GET_ONBOARDING_PRODUCT_STATUS, {
    fetchPolicy: "cache-and-network",
    skip: !canFetchProductStatus,
    notifyOnNetworkStatusChange: false,
  });
  const [setShipToCountry, { loading: isSavingShipToCountry }] = useMutation(SET_SHIP_TO_COUNTRY_AND_SETTING_FOURPX_SHIPPING_OPTIONS);
  const [saveCurrency, { loading: isSavingCurrency }] = useMutation(SAVE_SETTING_CURRENCY);

  const shippingReady = hasValue(currentShipToCountry) && hasValue(currentCurrency);
  const onboardingProductStatus = productStatusData?.getOnboardingProductStatus || null;
  const hasProducts = Number(onboardingProductStatus?.productCount ?? productCount ?? 0) > 0;
  const hasPushedProducts = Number(onboardingProductStatus?.pushedProductCount || 0) > 0;
  const hasConnectedExistingShopifyProducts = Number(onboardingProductStatus?.connectedExistingShopifyProductCount || 0) > 0;
  const extensionConnected = hasChromeExtension === true;
  const extensionLoggedIn = extensionConnected && isChromeExtensionLoggedIn === true;
  const subscriptionType = onboardingProductStatus?.subscriptionType || localStorage.getItem("subscriptionType") || "Free";
  const planActive = hasActiveSubscription(subscriptionType);
  const trialDays = Math.max(0, Number(onboardingProductStatus?.trialDays ?? storedTrialEligibility.trialDays ?? 0));
  const trialEligible = trialDays > 0;
  const trialEligibilityResolved = onboardingProductStatus != null || storedTrialEligibility.isFresh;
  const onboardingDefaultsAvailable = onboardingProductStatus != null
    && onboardingProductStatus.defaultFulfillmentOption != null
    && hasValue(onboardingProductStatus.priceCalculate)
    && onboardingProductStatus.ispublishingProduct != null
    && onboardingProductStatus.continueSellWhenOutOfStock != null;
  const defaultFulfillmentByCartifind = onboardingProductStatus?.defaultFulfillmentOption !== false;
  const publishesProductsAfterPush = onboardingProductStatus?.ispublishingProduct === true;
  const continuesSellingWhenOutOfStock = onboardingProductStatus?.continueSellWhenOutOfStock !== false;
  const priceCalculate = onboardingProductStatus?.priceCalculate || "suggested";
  const pricingSummary = priceCalculate === "times"
    ? `Cost × ${onboardingProductStatus?.timesValue || "2.00"}`
    : priceCalculate === "plus"
      ? `Cost + ${currentCurrency || "USD"} ${onboardingProductStatus?.plusValue || "10.00"}`
      : priceCalculate === "custom"
        ? "Custom pricing rules"
        : "Cartifind suggested pricing";

  useEffect(() => {
    if (onboardingProductStatus?.trialDays == null) return;
    localStorage.setItem("trialDays", String(Math.max(0, Number(onboardingProductStatus.trialDays || 0))));
    localStorage.setItem("trialEligibilityCheckedAt", String(Date.now()));
    localStorage.setItem("subscriptionType", onboardingProductStatus?.subscriptionType || "Free");
  }, [onboardingProductStatus]);

  const steps = useMemo(() => {
    const commonConnectedStep = {
      id: "shopify_connected",
      title: "Shopify store connected",
      description: "Cartifind is already running inside your Shopify admin.",
      complete: true
    };
    const settingsStep = {
      id: "settings_ready",
      title: "Choose shipping destination and currency",
      description: shippingReady ? "Your shipping destination and currency are saved." : "Set them here before importing products.",
      complete: shippingReady
    };
    const extensionStep = {
      id: "extension_connected",
      title: "Install and connect Cartifind Chrome Extension",
      description: extensionLoggedIn ? "Extension is installed and signed in." : extensionConnected ? "Extension is installed. Connect it with your Cartifind account." : "Required for Temu, SHEIN, 1688, Amazon, AliExpress, Etsy, Alibaba, and another Shopify store product page.",
      complete: extensionLoggedIn
    };
    const planStep = {
      id: "trial_started",
      title: trialEligible ? "Start your 7-day free trial" : "Choose a Cartifind plan",
      description: planActive
        ? "Your Cartifind plan is active."
        : trialEligible
          ? "Choose a monthly plan. Shopify shows the price and trial terms before you approve it."
          : "Shopify shows the price and billing terms before you approve a plan.",
      complete: planActive
    };
    const productDefaultsStep = {
      id: "product_defaults",
      title: "Review product defaults",
      description: "Confirm how new products will be fulfilled, priced, published, and sold when stock runs out.",
      complete: hasReviewedProductDefaults
    };

    if (selectedGoal === "fulfill_orders") {
      const hasOrders = Number(orderCount || 0) > 0;
      return [
        commonConnectedStep,
        {
          id: "open_orders",
          title: "Review existing Shopify orders",
          description: hasOrders ? "Orders are ready to review in My Orders." : "Open My Orders to check for fulfillable orders.",
          complete: hasOrders
        },
        ...(hasOrders ? [{
          id: "fulfill_first_order",
          title: "Fulfill your first order",
          description: "Open an order and use Cartifind to fulfill it through the supplier.",
          complete: false
        }] : []),
        planStep,
        extensionStep
      ];
    }

    if (selectedGoal === "sync_products") {
      return [
        commonConnectedStep,
        settingsStep,
        ...(onboardingDefaultsAvailable && !hasConnectedExistingShopifyProducts ? [productDefaultsStep] : []),
        {
          id: "open_products",
          title: "Select existing Shopify products",
          description: hasConnectedExistingShopifyProducts
            ? "Existing Shopify products are connected for Cartifind optimization."
            : "Select products in Shopify Admin and run Optimize with Cartifind.",
          complete: hasConnectedExistingShopifyProducts
        },
        ...(hasConnectedExistingShopifyProducts ? [{
          id: "sync_inventory",
          title: "Review supplier and inventory",
          description: "Connect a supplier before running a live inventory check or enabling Cartifind fulfillment.",
          complete: false
        }] : []),
        planStep,
        extensionStep
      ];
    }

    return [
      commonConnectedStep,
      settingsStep,
      ...(onboardingDefaultsAvailable && !hasProducts ? [productDefaultsStep] : []),
      {
        id: "first_import",
        title: "Import your first product",
        description: hasProducts ? "You have products ready to review." : "Start from product search or paste a supplier product URL.",
        complete: hasProducts
      },
      {
        id: "push_to_shopify",
        title: "Push product to Shopify",
        description: hasPushedProducts ? "At least one product has been pushed to Shopify." : hasProducts ? "Open My Products and push your first product to Shopify." : "This unlocks after the first product is imported.",
        complete: hasPushedProducts
      },
      planStep,
      extensionStep
    ];
  }, [extensionConnected, extensionLoggedIn, hasConnectedExistingShopifyProducts, hasProducts, hasPushedProducts, hasReviewedProductDefaults, onboardingDefaultsAvailable, orderCount, planActive, selectedGoal, shippingReady, trialEligible]);

  const completedSteps = steps.filter((step) => step.complete).length;
  const progress = Math.round((completedSteps / steps.length) * 100);
  const visibleSteps = steps.filter((step) => !step.complete);
  const nextStep = visibleSteps[0] || null;
  const laterSteps = visibleSteps.slice(1);
  const isOnboardingComplete = completedSteps === steps.length;
  const extensionStatusKnown = hasChromeExtension !== null;
  const looksEstablished = Number(productCount || 0) > 0 || Number(orderCount || 0) > 0 || planActive;
  const onboardingStatusPending = looksEstablished && (
    !extensionStatusKnown || onboardingProductStatus == null || isLoadingProductStatus
  );

  useEffect(() => {
    if (onboardingStatusPending) return;
    try {
      localStorage.setItem(ONBOARDING_GUIDE_VISIBILITY_KEY, isOnboardingComplete ? "0" : "1");
    } catch (_error) {
      // The in-memory first-paint decision still prevents a layout shift.
    }
  }, [isOnboardingComplete, onboardingStatusPending]);

  useEffect(() => {
    if (!guideVisible || onboardingStatusPending || isOnboardingComplete) return;
    if (sendLog == null || hasLoggedView.current) return;
    hasLoggedView.current = true;
    sendLog("ONBOARDING_VIEW", JSON.stringify({
      productCount: Number(productCount || 0),
      orderCount: Number(orderCount || 0),
      hasChromeExtension,
      subscriptionType,
      planActive,
      shippingReady,
      selectedGoal
    }));
  }, [guideVisible, sendLog, productCount, orderCount, hasChromeExtension, subscriptionType, planActive, shippingReady, selectedGoal, onboardingStatusPending, isOnboardingComplete]);

  useEffect(() => {
    if (sendLog == null || planActive || !trialEligibilityResolved || hasLoggedTrialView.current) return;
    hasLoggedTrialView.current = true;
    sendLog("ONBOARDING_TRIAL_VIEWED", JSON.stringify({
      trialDays,
      source: "finish_setup",
      selectedGoal,
    }));
  }, [planActive, selectedGoal, sendLog, trialDays, trialEligibilityResolved]);

  useEffect(() => {
    if (sendLog == null) return;
    [
      [true, "ONBOARDING_STEP_COMPLETE_SHOPIFY_CONNECTED"],
      [shippingReady, "ONBOARDING_STEP_COMPLETE_SETTINGS_READY"],
      [planActive, "ONBOARDING_PLAN_ACTIVE"],
      [extensionLoggedIn, "ONBOARDING_EXTENSION_CONNECTED"],
      [hasProducts, "ONBOARDING_FIRST_IMPORT_SUCCESS"],
      [hasPushedProducts, "ONBOARDING_FIRST_PUSH_SUCCESS"]
    ].forEach(([isComplete, eventName]) => {
      if (!isComplete || loggedStepEvents.current.has(eventName)) return;
      loggedStepEvents.current.add(eventName);
      sendLog(eventName);
    });
  }, [sendLog, shippingReady, planActive, extensionLoggedIn, hasProducts, hasPushedProducts]);

  const handleGoalSelect = (goal) => {
    setSelectedGoal(goal);
    setShowSetupPlan(false);
    setStoredGoal(goal);
    if (sendLog != null) {
      sendLog("ONBOARDING_GOAL_SELECTED", goal);
    }
  };

  const handleShipToCountryChange = (shipToCountry) => {
    setCurrentShipToCountry(shipToCountry);
    localStorage.setItem("shipToCountry", shipToCountry);
    if (sendLog != null) {
      sendLog("ONBOARDING_SETTINGS_SHIP_TO_COUNTRY_CHANGE", shipToCountry);
    }
    setShipToCountry({ variables: { shipToCountry } }).then((result) => {
      if (result?.data?.setShipToCountryAndSettingFourPXShippingOptions?.status) {
        shopify.toast.show(i18("Ship to country is updated"));
      } else {
        shopify.toast.show(i18("Failed to update ship-to country"), { isError: true });
      }
    }).catch(() => {
      shopify.toast.show(i18("Failed to update ship-to country"), { isError: true });
    });
  };

  const handleCurrencyChange = (currency) => {
    setCurrentCurrency(currency);
    localStorage.setItem("currency", currency);
    if (sendLog != null) {
      sendLog("ONBOARDING_SETTINGS_CURRENCY_CHANGE", currency);
    }
    saveCurrency({ variables: { currency } }).then((result) => {
      if (result?.data?.saveSettingProduct?.status) {
        shopify.toast.show(i18("Currency is updated"));
      } else {
        shopify.toast.show(i18("Failed to update currency"), { isError: true });
      }
    }).catch(() => {
      shopify.toast.show(i18("Failed to update currency"), { isError: true });
    });
  };

  const handleConnectExtension = useCallback(async (options = {}) => {
    const automatic = options?.automatic === true;
    if (isConnectingExtensionRef.current) return;

    if (sendLog != null) {
      if (automatic) {
        sendLog("ONBOARDING_EXTENSION_CONNECT_CLICK", "auto");
      } else {
        sendLog("ONBOARDING_EXTENSION_INSTALL_CLICK", "setup_guide");
        sendLog("ONBOARDING_EXTENSION_CONNECT_CLICK");
      }
    }

    isConnectingExtensionRef.current = true;
    autoConnectAttemptedRef.current = true;
    setIsConnectingExtension(true);
    setExtensionConnectionMessage(i18("Connecting to the Chrome Extension..."));
    let detectedExtension = null;
    let extensionKey = null;
    try {
      const [{ checkChromeExtensionVersion, connectChromeExtensionWithKey }, extensionKeyResult] = await Promise.all([
        import("../utils/chromeExtensionUtils.js"),
        loadUserExtensionKey(),
      ]);
      extensionKey = extensionKeyResult?.data?.getUserExtensionKey;
      if (!extensionKey) {
        throw new Error("CHROME_EXTENSION_KEY_UNAVAILABLE");
      }
      detectedExtension = await checkChromeExtensionVersion({
        extensionId: chromeExtensionMessageTargetID,
        timeout: 3000,
      });
      if (detectedExtension?.installed !== true) {
        throw new Error("CHROME_EXTENSION_NOT_DETECTED");
      }
      if (
        isShopifyTestHost &&
        detectedExtension?.version &&
        detectedExtension.version !== chromeExtensionExpectedVersion
      ) {
        throw new Error("CHROME_EXTENSION_TEST_VERSION_MISMATCH");
      }
      await connectChromeExtensionWithKey({
        extensionId: chromeExtensionMessageTargetID,
        extensionKey,
        timeout: 15000
      });
      setState((previousState) => ({
        ...previousState,
        hasChromeExtension: true,
        isChromeExtensionLoggedIn: true
      }));
      setExtensionConnectionMessage(i18("Chrome Extension connected"));
      shopify.toast.show(i18("Chrome Extension connected"));
      if (sendLog != null) {
        sendLog("ONBOARDING_EXTENSION_CONNECTED", "auto_connect");
      }
      return;
    } catch (error) {
      if (automatic && detectedExtension?.installed !== true) {
        setExtensionConnectionMessage("");
        return;
      }
      if (sendLog != null && !automatic) {
        sendLog("ONBOARDING_EXTENSION_COPY_KEY_AND_OPEN_CLICK");
      }
      const [{ buildChromeExtensionTroubleshootingMessage }] = await Promise.all([
        import("../utils/chromeExtensionUtils.js"),
      ]);
      setExtensionConnectionMessage(i18(buildChromeExtensionTroubleshootingMessage({
        isTestHost: isShopifyTestHost,
        expectedVersion: chromeExtensionExpectedVersion,
        detectedVersion: detectedExtension?.version,
        detectedExtensionId: detectedExtension?.extensionId,
        extensionErrorCode: error?.code || null,
        extensionErrorMessage: error?.message || null,
      })));
      if (automatic) {
        return;
      }
      if (extensionKey && navigator.clipboard) {
        navigator.clipboard.writeText(extensionKey).then(() => {
          shopify.toast.show(i18("Extension key copied. Install or open the extension to finish connecting."));
        }).catch(() => {
          shopify.toast.show(i18("Open the extension and paste your key to connect."), { isError: true });
        });
      }
      if (!isShopifyTestHost) {
        openExternalUrl(CHROME_EXTENSION_URL);
      }
    } finally {
      isConnectingExtensionRef.current = false;
      setIsConnectingExtension(false);
    }
  }, [loadUserExtensionKey, sendLog, setState, shopify]);

  useEffect(() => {
    if (isMobile) return;
    if (isChromeExtensionLoggedIn === true) return;
    if (autoConnectAttemptedRef.current || isConnectingExtensionRef.current) return;
    if (hasChromeExtension !== true || isChromeExtensionLoggedIn !== false) return;
    autoConnectAttemptedRef.current = true;
    handleConnectExtension({ automatic: true });
  }, [handleConnectExtension, hasChromeExtension, isChromeExtensionLoggedIn, isMobile]);

  const handleStartTrial = () => {
    if (sendLog != null) {
      sendLog("ONBOARDING_TRIAL_CLICK", JSON.stringify({
        trialDays,
        source: "finish_setup",
        selectedGoal,
      }));
    }
    shopify.toast.show(i18(trialEligible ? "Opening plans with the 7-day free trial." : "Opening plans."));
    navigate(`/changePlan?ref=productSearch&intent=${encodeURIComponent(selectedGoal || "first_import")}`);
  };

  const handleImportByURL = () => {
    if (sendLog != null) {
      sendLog("ONBOARDING_FIRST_IMPORT_STARTED", "import_by_url");
    }
    if (onOpenImportByURL != null) {
      onOpenImportByURL();
    } else {
      navigate("/importByURL");
    }
  };

  const handleBrowseProducts = () => {
    if (sendLog != null) {
      sendLog("ONBOARDING_FIRST_IMPORT_STARTED", "product_search");
    }
    navigate("/productSearch");
  };

  const handlePushToShopify = () => {
    if (sendLog != null) {
      sendLog("ONBOARDING_FIRST_PUSH_STARTED");
    }
    navigate("/myProducts");
  };

  const handleOpenOrders = () => {
    if (sendLog != null) {
      sendLog("ONBOARDING_OPEN_ORDERS_CLICK");
    }
    navigate("/MyOrders");
  };

  const handleOpenProducts = (eventName) => {
    if (sendLog != null) {
      sendLog(eventName);
    }
    navigate("/myProducts");
  };

  const handleOpenShopifyProducts = () => {
    if (sendLog != null) sendLog("ONBOARDING_SELECT_EXISTING_SHOPIFY_PRODUCTS_CLICK");
    shopify.toast.show(i18("Opening Shopify products."));
    const shopDomain = String(shopify?.config?.shop || localStorage.getItem("shopName") || "")
      .replace(/^https?:\/\//, "")
      .replace(/\.myshopify\.com.*$/, "")
      .replace(/\/.*$/, "");
    if (shopDomain) {
      openShopifyPageInNewWindow(`https://admin.shopify.com/store/${shopDomain}/products`);
      return;
    }
    shopify.toast.show(i18("Shopify products could not be opened."), { isError: true });
  };

  const completeProductDefaultsReview = (source) => {
    setStoredDefaultsReviewed();
    setHasReviewedProductDefaults(true);
    if (sendLog != null) {
      sendLog("ONBOARDING_PRODUCT_DEFAULTS_REVIEWED", JSON.stringify({
        source,
        defaultFulfillmentOption: defaultFulfillmentByCartifind,
        priceCalculate,
        ispublishingProduct: publishesProductsAfterPush,
        continueSellWhenOutOfStock: continuesSellingWhenOutOfStock,
      }));
    }
  };

  const handleUseProductDefaults = () => {
    completeProductDefaultsReview("accepted");
    shopify.toast.show(i18("Product defaults confirmed."));
  };

  const handleReviewProductDefaults = () => {
    completeProductDefaultsReview("opened_settings");
    navigate("/setting?tabShow=product&ref=onboarding");
  };

  const renderStepAction = (step) => {
    if (!step) return null;

    if (step.id === "extension_connected" && !extensionLoggedIn) {
      return (
        <BlockStack gap="100">
          <Box>
            <Button icon={ClipboardIcon} variant="primary" loading={isConnectingExtension} onClick={() => handleConnectExtension()}>
              {i18("Connect extension")}
            </Button>
          </Box>
          {extensionConnectionMessage ? (
            <Text as="p" variant="bodySm" tone="subdued">{extensionConnectionMessage}</Text>
          ) : hasChromeExtension == null && !isMobile ? (
            <Text as="p" variant="bodySm" tone="subdued">{i18("Checking Chrome Extension connection...")}</Text>
          ) : null}
        </BlockStack>
      );
    }

    if (step.id === "trial_started" && !planActive) {
      return <Button variant="primary" onClick={handleStartTrial}>{i18(trialEligible ? "Start free trial" : "View plans")}</Button>;
    }

    if (step.id === "settings_ready" && !shippingReady) {
      return (
        <InlineStack gap="200" blockAlign="end" wrap>
          <Box style={{ minWidth: "min(180px, 100%)", flex: "1 1 180px" }}>
            <Select
              label={i18("Ship to")}
              value={currentShipToCountry}
              disabled={isSavingShipToCountry}
              options={countryList.map((item) => ({ label: item.option, value: item.value }))}
              onChange={handleShipToCountryChange}
              placeholder={i18("Select country")}
            />
          </Box>
          <Box style={{ minWidth: "min(180px, 100%)", flex: "1 1 180px" }}>
            <Select
              label={i18("Currency")}
              value={currentCurrency}
              disabled={isSavingCurrency}
              options={majorCurrencyList.map((item) => ({ label: item.option, value: item.value }))}
              onChange={handleCurrencyChange}
              placeholder={i18("Select currency")}
            />
          </Box>
        </InlineStack>
      );
    }

    if (step.id === "product_defaults") {
      return (
        <BlockStack gap="300">
          <Box background="bg-surface" borderRadius="200" padding="300">
            <BlockStack gap="200">
              <InlineStack align="space-between" gap="300" wrap>
                <Text as="span" tone="subdued">{i18("Fulfillment")}</Text>
                <Text as="span" fontWeight="semibold">
                  {i18(defaultFulfillmentByCartifind ? "Fulfilled by Cartifind" : "Fulfilled by you")}
                </Text>
              </InlineStack>
              <InlineStack align="space-between" gap="300" wrap>
                <Text as="span" tone="subdued">{i18("Pricing")}</Text>
                <Text as="span" fontWeight="semibold">{i18(pricingSummary)}</Text>
              </InlineStack>
              <InlineStack align="space-between" gap="300" wrap>
                <Text as="span" tone="subdued">{i18("After pushing")}</Text>
                <Text as="span" fontWeight="semibold">
                  {i18(publishesProductsAfterPush ? "Publish to Online Store" : "Keep as draft")}
                </Text>
              </InlineStack>
              <InlineStack align="space-between" gap="300" wrap>
                <Text as="span" tone="subdued">{i18("When sold out")}</Text>
                <Text as="span" fontWeight="semibold">
                  {i18(continuesSellingWhenOutOfStock ? "Continue selling" : "Stop selling")}
                </Text>
              </InlineStack>
            </BlockStack>
          </Box>
          <ButtonGroup>
            <Button variant="primary" onClick={handleUseProductDefaults}>{i18("Use these defaults")}</Button>
            <Button onClick={handleReviewProductDefaults}>{i18("Change defaults")}</Button>
          </ButtonGroup>
        </BlockStack>
      );
    }

    if (step.id === "first_import" && !hasProducts) {
      return (
        <ButtonGroup>
          <Button icon={ImportIcon} variant="primary" onClick={handleImportByURL}>{i18("Import by URL")}</Button>
          <Button icon={SearchIcon} onClick={handleBrowseProducts}>{i18("Search products")}</Button>
        </ButtonGroup>
      );
    }

    if (step.id === "push_to_shopify" && hasProducts && !hasPushedProducts) {
      return <Button icon={StoreImportIcon} variant="primary" onClick={handlePushToShopify}>{i18("Push to Shopify")}</Button>;
    }

    if (step.id === "open_orders" || step.id === "fulfill_first_order") {
      return <Button variant="primary" onClick={handleOpenOrders}>{i18("Open My Orders")}</Button>;
    }

    if (step.id === "open_products") {
      return <Button icon={ProductReferenceIcon} variant="primary" onClick={handleOpenShopifyProducts}>{i18("Select Shopify products")}</Button>;
    }

    if (step.id === "sync_inventory") {
      return <Button variant="primary" onClick={() => handleOpenProducts("ONBOARDING_SYNC_PRODUCTS_CLICK")}>{i18("Open My Products")}</Button>;
    }

    return null;
  };

  if (!guideVisible) {
    return null;
  }

  return (
    <Box className="cartifind-get-started-slot" paddingBlockEnd="500">
      <Card>
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="start" gap="400" wrap>
            <BlockStack gap="100">
              <Text as="h2" variant="headingLg">{i18(isOnboardingComplete ? "Setup complete" : "Finish setup")}</Text>
              <Text as="p" tone="subdued">{i18(isOnboardingComplete ? "Every setup step for this goal is done." : "One clear next step, based on what you want to do.")}</Text>
            </BlockStack>
            <Box style={{ width: "min(320px, 100%)" }}>
              <Select
                label={i18("Your goal")}
                labelInline
                value={selectedGoal}
                options={[
                  { label: i18("Import products"), value: "import_products" },
                  { label: i18("Fulfill existing orders"), value: "fulfill_orders" },
                  { label: i18("Optimize Shopify products"), value: "sync_products" }
                ]}
                onChange={handleGoalSelect}
              />
            </Box>
          </InlineStack>

          <BlockStack gap="200">
            <InlineStack align="space-between" blockAlign="center">
              <Text as="span" variant="bodyMd">{i18("${completed} of ${total} steps complete", { completed: completedSteps, total: steps.length })}</Text>
              <Text as="span" tone="subdued">{progress}%</Text>
            </InlineStack>
            <ProgressBar progress={progress} size="small" tone="primary" />
          </BlockStack>

          {nextStep ? (
            <Box background="bg-surface-secondary" borderRadius="300" padding="400">
              <BlockStack gap="300">
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued" fontWeight="semibold">{i18("Next step")}</Text>
                  <Text as="h3" variant="headingMd">{i18(nextStep.title)}</Text>
                  <Text as="p" tone="subdued">{i18(nextStep.description)}</Text>
                </BlockStack>
                <Box>{renderStepAction(nextStep)}</Box>
              </BlockStack>
            </Box>
          ) : null}

          {laterSteps.length > 0 ? (
            <BlockStack gap="200">
              <Box>
                <Button
                  variant="plain"
                  disclosure={showSetupPlan ? "up" : "down"}
                  ariaExpanded={showSetupPlan}
                  onClick={() => setShowSetupPlan((isOpen) => !isOpen)}
                >
                  {showSetupPlan
                    ? i18("Hide setup plan")
                    : i18("View %count% later setup steps").replace("%count%", String(laterSteps.length))}
                </Button>
              </Box>
              <Collapsible open={showSetupPlan} id="cartifind-setup-plan" transition={{ duration: "200ms", timingFunction: "ease-in-out" }}>
                <BlockStack gap="200">
                  <Divider />
                  {laterSteps.map((step) => (
                    <InlineStack key={step.id} gap="200" blockAlign="start" wrap>
                      <Box paddingBlockStart="050">
                        <Icon source={ProductReferenceIcon} tone="subdued" />
                      </Box>
                      <BlockStack gap="050">
                        <Text as="p" variant="bodyMd" fontWeight="semibold">{i18(step.title)}</Text>
                        <Text as="p" variant="bodySm" tone="subdued">{i18(step.description)}</Text>
                      </BlockStack>
                    </InlineStack>
                  ))}
                </BlockStack>
              </Collapsible>
            </BlockStack>
          ) : null}
        </BlockStack>
      </Card>
    </Box>
  );
}
